import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState("light");
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Initialize theme on client-side only
  useEffect(() => {
    if (globalThis.window !== undefined) {
      // Check if SSR script has already initialized theme
      if (globalThis.window.__THEME_INITIALIZED__) {
        const existingTheme = document.body.dataset.theme;
        if (
          existingTheme &&
          (existingTheme === "light" || existingTheme === "dark")
        ) {
          setTheme(existingTheme);
          return;
        }
      }

      // Fallback to localStorage or system preference
      const savedTheme = localStorage.getItem("theme");
      if (savedTheme && (savedTheme === "light" || savedTheme === "dark")) {
        setTheme(savedTheme);
      } else {
        const prefersDark = globalThis.window.matchMedia(
          "(prefers-color-scheme: dark)",
        ).matches;
        const systemTheme = prefersDark ? "dark" : "light";
        setTheme(systemTheme);
      }
    }
  }, []);

  // Update DOM and localStorage when theme changes
  useEffect(() => {
    if (globalThis.window !== undefined) {
      setIsTransitioning(true);
      document.documentElement.dataset.theme = theme;
      document.body.dataset.theme = theme;
      localStorage.setItem("theme", theme);

      // Reset transition state after animation completes
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  const value = useMemo(() => ({
    theme,
    toggleTheme,
    isTransitioning,
  }), [theme, isTransitioning]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
